import { checkFileExtension, detectDeviceInfo } from 'src/utils/util';
import { convertFileToBase64, getDeviceType, getBrowserType } from '../utils/util';
import axios from 'src/api/Api';
import { BASESITEID } from 'src/constants/appConstants';

const baseSiteId = localStorage.getItem('baseSiteId');

const url = `occ/v2/${baseSiteId}/issueApi/reportIssue`;
    
export const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    const imageTypes = ['jpg', 'jpeg', 'gif', 'tif', 'tiff', 'bmp', 'png', 'heic'];
    
    if (imageTypes.includes(ext)) {
        return 'image';
    } else if (ext === 'pdf') {
        return 'pdf';
    } else if (['doc', 'docx'].includes(ext)) {
        return 'doc';
    } else if (['xls', 'xlsx'].includes(ext)) {
        return 'xls';
    } else if (ext === 'txt') {
        return 'txt';
    } else if (ext === 'html') {
        return 'html';
    } else if (ext === 'msg') {
        return 'msg';
    }
    return 'file';
};

export const filesValidation = (files, t, setFileValidationError) => {
    let fileSizeError = false;
    let fileExtensionError = false;
    
    for (let i = 0; i < files.length; i++) {
        if (!checkFileExtension(files[i].name)) {
            fileExtensionError = true;
            setFileValidationError(t('reportIssue.pleaseselectextension'));
            break;
        }
        if (files[i].size >= 10485760) {
            fileSizeError = true;
            setFileValidationError(t('reportIssue.filesizenot10mb'));
            break;
        }
    }
    
    if (!fileExtensionError && !fileSizeError) {
        setFileValidationError('');
    }
    
    return !fileExtensionError && !fileSizeError;
};

export const handleFileChange = (e, setAttachedFiles) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
        const newFiles = files.map(file => {
            const fileType = getFileIcon(file.name);
            return {
                file: file,
                name: file.name,
                size: file.size,
                type: fileType,
                preview: fileType === 'image' ? URL.createObjectURL(file) : null
            };
        });
        setAttachedFiles(prev => [...prev, ...newFiles]);
    } 
    e.target.value = '';
};

export const handleDeleteFile = (index, setAttachedFiles) => {
    setAttachedFiles(prev => {
        const updated = [...prev]; 
        if (updated[index].preview) {
            URL.revokeObjectURL(updated[index].preview);
        }
        updated.splice(index, 1);
        return updated;
    });
};

export const handleViewFile = (file, setPreviewImage) => {
    // Only show preview for previewable file types (images, PDFs)
    if (file.type === 'image' || file.type === 'pdf') {
        setPreviewImage(file);
    } else {
        // For non-previewable files (doc, xls, msg, etc.), trigger download
        const url = URL.createObjectURL(file.file);
        const link = document.createElement('a');
        link.href = url;
        link.download = file.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 100);
    }
};

export const handleClosePreview = (setPreviewImage, setDocViewerUrl) => {
    setPreviewImage(null);
    setDocViewerUrl(null);
};

export const handleSubmitReport = async ({
    issueDescription,
    attachedFiles,
    userDetails,
    emailCopy,
    // myCaseRef,
    setEmailCopy,
    setIsSubmitting,
    setShowSuccessMessage,
    setIssueDescription,
    setAttachedFiles
}) => {
    if (!issueDescription.trim()) {
        alert('Please enter issue details');
        return;
    }

    setIsSubmitting(true);

    try {
        // Get baseSiteId - if null, wait for it to be available
        let baseSiteId = localStorage.getItem('baseSiteId');
        
        if (!baseSiteId) {
            const waitForBaseSiteId = () => {
                return new Promise((resolve) => {
                    const checkBaseSiteId = () => {
                        const baseSiteId = localStorage.getItem('baseSiteId');
                        if (baseSiteId) {
                            resolve(baseSiteId);
                        } else {
                            setTimeout(checkBaseSiteId, 100); // Check every 100ms
                        }
                    };
                    checkBaseSiteId();
                });
            };
            
            baseSiteId = await waitForBaseSiteId();
        }
        
        const url = `occ/v2/${baseSiteId}/issueApi/reportIssue`;
        
        const base64Images = await Promise.all(
            attachedFiles.map(async (fileObj) => {
                const base64 = await convertFileToBase64(fileObj.file);
                const fileType = fileObj.file.type || 'application/octet-stream';
                const fileName = fileObj.name;
                // Format: "base64data,filetype,filename"
                return `${base64},${fileType},${fileName}`;
            })
        );

        // Get salesOrg - handle both string and array cases
        let salesOrg = null;
        if (userDetails?.salesOrgs) {
            // Check if salesOrgs is an array or a string
            salesOrg = Array.isArray(userDetails.salesOrgs) 
                ? userDetails.salesOrgs[0]
                : userDetails.salesOrgs;
        }
        
        // Fallback to localStorage if not found in userDetails
        if (!salesOrg) {
            const storedSalesOrg = localStorage.getItem('salesOrg');
            try {
                // Try to parse as JSON array
                const parsed = JSON.parse(storedSalesOrg);
                salesOrg = Array.isArray(parsed) ? parsed[0] : storedSalesOrg;
            } catch {
                // If not JSON, use as string
                salesOrg = storedSalesOrg;
            }
        }

        const { deviceType, browser } = detectDeviceInfo();
        const orderId = localStorage.getItem('myCaseRefId');
        const reportData = {
            networkID: userDetails?.networkId || localStorage.getItem('NetworkId'),
            salesOrg: salesOrg,
            issueDescription: issueDescription,
            browserType: browser,
            deviceType: deviceType,
            orderInformation: orderId || '',
            currentPageAction: localStorage.getItem('pageName') || window.location.pathname || 'home',
            base64Images: base64Images, // Array of strings in format "base64data,filetype,filename"
            submissionDateTime: new Date().toISOString(),
            sendCopy: emailCopy,
        };
 
        const response = await axios.post(url, reportData, {
            headers: {
                'Content-Type': 'application/json',
            }
        });
 
        if (response.status === 200 || response.status === 201) {
            setShowSuccessMessage(true);
            setIssueDescription('');
            setEmailCopy(false);
            setAttachedFiles([]);
        }
        
    } catch (error) {
        console.error('Error submitting report:', error);
        console.error('Error response:', error.response?.data); 
        const errorMessage = error.response?.data?.message || 'Failed to submit report. Please try again.';
    } finally {
        setIsSubmitting(false);
    }
};